import { useState } from "react";

import { IconButton, Menu, MenuItem, Stack } from "@mui/material";

import { ArrowClockwise, DotsThreeVertical, UserSwitch } from "@phosphor-icons/react";
import { doc, getFirestore, updateDoc } from "firebase/firestore";

import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { useLessonTimer } from "@/hooks/useLessonTimer";
import { Lesson, UserRole } from "@/types";

import HeaderActions from "../HeaderActions";
import LessonDetails from "../LessonDetails";

export interface ConferenceHeaderProps {
  userName: string | undefined;
  userPhoto: string | undefined;
  userId: string | undefined;
  lessonInfo: Lesson | null;
  lessonId: string | undefined;
  onLessonEnd: () => void;
  onProceed: () => void;
  isSandbox?: boolean;
}

function ConferenceHeader({
  userName,
  userPhoto,
  userId,
  lessonInfo,
  lessonId,
  onLessonEnd,
  onProceed,
  isSandbox = false,
}: ConferenceHeaderProps) {
  const { remainingMinutes, progressPercent } = useLessonTimer(lessonInfo, isSandbox);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { userRole } = useFirebaseContext();
  const db = getFirestore();

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchRole = () => {
    const newRole = userRole === UserRole.Tutor ? UserRole.Student : UserRole.Tutor;
    const url = new URL(window.location.href);
    url.searchParams.set("role", newRole);
    window.location.href = url.toString();
  };

  const handleClearRealtimeResources = async () => {
    if (lessonId) {
      try {
        await updateDoc(doc(db, "lessonInteractionProfiles", lessonId), {
          realtimeResources: {
            problems: [],
            examples: [],
            diagrams: [],
          },
        });
        console.log("Realtime resources cleared");
      } catch (err) {
        console.error("Failed to clear realtime resources:", err);
      }
    }
    handleCloseMenu();
  };

  return (
    <Stack
      sx={{
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        px={3.5}
        py={1}
      >
        {lessonInfo ? (
          <LessonDetails
            lessonInfo={lessonInfo}
            participantName={userName}
            participantPhoto={userPhoto}
            participantUserId={userId}
            remainingMinutes={remainingMinutes}
            progressPercent={progressPercent}
          />
        ) : (
          <Stack flex={1} />
        )}
        <Stack direction="row" alignItems="center" gap={2} sx={{ flexShrink: 0 }}>
          <HeaderActions
            lessonInfo={lessonInfo}
            lessonId={lessonId || ""}
            onLessonEnd={onLessonEnd}
            onProceed={onProceed}
            isSandbox={isSandbox}
          />
          {isSandbox && (
            <>
              <IconButton onClick={handleOpenMenu} sx={{ color: "secondary.main" }}>
                <DotsThreeVertical size={24} weight="bold" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                <MenuItem onClick={handleSwitchRole} sx={{ gap: 1 }}>
                  <UserSwitch size={20} />
                  Switch to {userRole === UserRole.Tutor ? "Student" : "Tutor"} View
                </MenuItem>
                <MenuItem onClick={handleClearRealtimeResources} sx={{ gap: 1 }}>
                  <ArrowClockwise size={20} />
                  Reset Luna
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ConferenceHeader;
